import { ChatUIConfig } from "./types";

type ProductCardPayload = {
    title: string;
    price: string;
    url?: string | null;
    handle?: string | null;
    imageUrl?: string | null;
};

export class ExcellyMessenger extends HTMLElement {
    private audioManager: any;
    private chatUI: any;
    private signalR: any;
    private sessionId: string | null = null;
    private history: any[] = [];
    private name = "";
    private email = "";
    private phone = "";
    private agentId: string | null = null;
    private agentName: string | null = null;
    private selectedPersonaId: string | null = null;
    private initialSubmit = true;
    private isUIInitialized = false;
    private audioChatSessionId: string | null = null;
    private baseUrl = "";
    private chatUIModule: any;
    private audioModule: any;
    private displayWelcomeScreen = false;
    private isUserDetailsSubmitted = false;
    private isStreamingAssistant = false;
    private isVoiceResponse = false;
    private isCurrentSessionVoice = false;
    private isVoiceAssistantRendering = false;
    private voiceCallAborted = false;
    private pendingVoiceProductCards: ProductCardPayload[] | null = null;
    private lastProductCardsSignature: string | null = null;
    private lastProductCardsAtMs = 0;
    private welcomeMessage = "";
    private streamingAssistantBuffer = "";
    private pendingAssistantTokenBuffer = "";
    private assistantStreamFrameRequestId: number | null = null;
    private suppressAssistantStream = false;
    private toolCallVoicePromptDebounceId: number | null = null;
    private lastToolCallVoicePromptAtMs = 0;
    private lastToolCallVoicePromptIndex = -1;
    private activeVoiceRenderSessionId = 0;
    private voiceRenderCleanupTimeoutId: number | null = null;
    private voiceRenderWordTimeoutIds: number[] = [];
    private voiceRenderFinalizeInFlight = false;
    private activeVoiceRenderMessage: string | null = null;
    private activeVoiceRenderMessageSessionId: number | null = null;
    private activeVoiceRenderPersisted = false;
    private readonly toolCallVoicePrompts = [
        "Let me take a closer look and find the right details for you.",
        "I'm on it — gathering the most relevant information now.",
        "Give me a moment while I connect the dots for you.",
        "I'm checking this carefully so I can give you a solid answer.",
        "Looking into that now — I'll have something useful in just a moment.",
        "I'm digging through the details to make sure this is accurate.",
        "One moment — I'm pulling together the best answer for you.",
        "I'm working behind the scenes on that request right now.",
        "Just a second, I am checking that for you.",
        "Hang on a moment, I am finalizing this for you.",
    ];
    private readonly defaultLoadingPhraseSets = [
        [
            "Let me take a look at that…",
            "Putting the pieces together…",
            "Thinking through the best answer…",
            "Almost ready for you…",
        ],
        [
            "Reading your message carefully…",
            "Organizing a helpful response…",
            "Making sure everything makes sense…",
            "Just finishing up…",
        ],
        [
            "Working on this for you…",
            "Connecting the dots…",
            "Summarizing what matters most…",
            "Here it comes…",
        ],
    ];
    private readonly toolCallLoadingPhraseSets = [
        [
            "Reaching out to the right service…",
            "Bringing in the latest information…",
            "Going through the results…",
            "Making sure everything looks right…",
            "Almost done…",
        ],
        [
            "Connecting things behind the scenes…",
            "Running the request now…",
            "Reviewing what came back…",
            "Checking for anything new…",
            "Wrapping it up for you…",
        ],
        [
            "Talking to external tools…",
            "Syncing everything together…",
            "Collecting the response…",
            "Double-checking the details…",
            "Just about ready…",
        ],
    ];

    constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    private getRandomPhraseSet(sets: string[][]): string[] {
        if (sets.length === 0) return [];
        const index = Math.floor(Math.random() * sets.length);
        return sets[index];
    }

    private getOrderedPhrasesFromRandomLists(sets: string[][]): string[] {
        const validSets = sets.filter(
            (set) => Array.isArray(set) && set.length > 0,
        );
        if (validSets.length === 0) return [];

        const maxLength = Math.max(...validSets.map((set) => set.length));
        const orderedPhrases: string[] = [];

        for (let i = 0; i < maxLength; i++) {
            const candidates = validSets
                .map((set) => set[i])
                .filter(
                    (phrase): phrase is string =>
                        !!phrase && phrase.trim().length > 0,
                );

            if (candidates.length === 0) continue;

            const randomIndex = Math.floor(Math.random() * candidates.length);
            orderedPhrases.push(candidates[randomIndex]);
        }

        return orderedPhrases;
    }

    private getModulePath(baseUrl: string, moduleName: string): string {
        const cleanBaseUrl = baseUrl.replace(/\/$/, "");
        return `${cleanBaseUrl}/${moduleName}.js`;
    }

    private async fetchModule(path: string) {
        try {
            const response = await fetch(path, {
                method: "GET",
                headers: {
                    Accept: "application/javascript",
                },
            });

            if (!response.ok) {
                const credentialResponse = await fetch(path, {
                    method: "GET",
                    credentials: "same-origin",
                    headers: {
                        Accept: "application/javascript",
                    },
                });

                if (!credentialResponse.ok) {
                    throw new Error(
                        `HTTP error! status: ${credentialResponse.status}`,
                    );
                }

                const moduleText = await credentialResponse.text();
                const blob = new Blob([moduleText], {
                    type: "application/javascript",
                });
                const moduleUrl = URL.createObjectURL(blob);

                try {
                    const module = await import(moduleUrl);
                    URL.revokeObjectURL(moduleUrl);
                    return module;
                } catch (error) {
                    URL.revokeObjectURL(moduleUrl);
                    throw error;
                }
            }

            const moduleText = await response.text();
            const blob = new Blob([moduleText], {
                type: "application/javascript",
            });
            const moduleUrl = URL.createObjectURL(blob);

            try {
                const module = await import(moduleUrl);
                URL.revokeObjectURL(moduleUrl);
                return module;
            } catch (error) {
                URL.revokeObjectURL(moduleUrl);
                throw error;
            }
        } catch (error) {
            console.error(`Failed to load module at ${path}:`, error);
            throw new Error(`Module loading failed: ${path}`);
        }
    }

    async connectedCallback() {
        try {
            this.baseUrl =
                this.getAttribute("baseUrl") ||
                "https://api.blipsquad.com/chatbot";
            const personaId = this.getAttribute("personaId");
            const workspaceId = this.getAttribute("workspaceId") || "";
            this.selectedPersonaId = personaId;
            this.sessionId = this.getOrSetSessionId();

            const modules = {
                audio: this.getModulePath(this.baseUrl, "audio/AudioManager"),
                chatUI: this.getModulePath(this.baseUrl, "ui/ChatUI"),
                signalR: this.getModulePath(
                    this.baseUrl,
                    "signalr/SignalRConnection",
                ),
            };

            const [audioModule, chatUIModule, signalRModule] =
                await Promise.all([
                    this.fetchModule(modules.audio).catch((error) => {
                        console.error("Failed to load audio module:", error);
                        return null;
                    }),
                    this.fetchModule(modules.chatUI).catch((error) => {
                        console.error("Failed to load ChatUI module:", error);
                        return null;
                    }),
                    this.fetchModule(modules.signalR).catch((error) => {
                        console.error("Failed to load SignalR module:", error);
                        return null;
                    }),
                ]);

            if (chatUIModule) {
                this.chatUIModule = chatUIModule;
            }

            if (signalRModule) {
                this.signalR = new signalRModule.SignalRConnection(
                    this.baseUrl,
                    this.sessionId,
                    this.getAttribute("workspaceId") || "",
                    this.selectedPersonaId,
                );
            }

            if (audioModule) {
                this.audioModule = audioModule;
            }

            if (!this.chatUIModule || !this.signalR) {
                throw new Error(
                    "Required modules (ChatUI or SignalR) failed to load",
                );
            }

            if (this.selectedPersonaId) {
                await this.fetchPersonaConfig();
            } else {
                throw new Error("Persona ID is required");
            }
        } catch (error) {
            console.error("Failed to initialize ExcellyMessenger:", error);
            this.handleInitializationError();
        }
    }

    private async fetchPersonaConfig() {
        try {
            const response = await fetch(
                `${new URL(this.baseUrl).origin + "/api/"}personaConfig/${this.selectedPersonaId}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const { name, config } = data;

            if (this.sessionId) {
                this.history = this.loadChatHistory(this.sessionId);
            } else {
                this.history = [];
            }

            this.displayWelcomeScreen = config.displayWelcomeScreen;
            this.welcomeMessage = config.welcomeMessage || "";
            await this.initializeSignalRConnection();

            if (!this.isUIInitialized) {
                try {
                    const chatConfig = {
                        baseUrl: this.getAttribute("baseUrl") || "",
                        title: name,
                        logoUrl: config.logoUrl,
                        displayTitle: config.displayTitle,
                        displayWelcomeScreen: config.displayWelcomeScreen,
                        isToggleIcon: config.isToggleIcon,
                        toggleImage: config.toggleImage,
                        voiceChat: config.voiceChat,
                        primaryColor: config.primaryColor,
                        secondaryColor: config.secondaryColor,
                        tertiaryColor: config.tertiaryColor,
                        templateType: config.templateType,
                        interfaceSize: config.interfaceSize,
                        attentionGrabberUrl: config.attentionGrabberUrl,
                        suggestedQuestions: config.suggestedQuestions,
                        welcomeMessage: config.welcomeMessage,
                        placeHolder: config.placeHolder,
                        subTitle: config.subTitle,
                        isCircularLogo: config.isCircularLogo,
                        showLogo: config.showLogo,
                        hideBranding: config.hideBranding,
                        customBranding: config.customBranding,
                        customBrandingUrl: config.customBrandingUrl,
                        onSubmitSessionData: async (
                            name: string,
                            email: string,
                            phone: string,
                        ) => {
                            this.name = name;
                            this.email = email;
                            this.phone = phone;
                            this.isUserDetailsSubmitted = true;
                            this.chatUI.showTypingIndicator(
                                this.getRandomPhraseSet(
                                    this.defaultLoadingPhraseSets,
                                ),
                            );
                            await this.signalR.submitSessionData(
                                this.selectedPersonaId || "",
                                name,
                                email,
                                phone,
                            );
                            if (this.displayWelcomeScreen) {
                                const message = config.welcomeMessage;
                                this.chatUI.hideTypingIndicator();
                                // Don't append welcome message for modern template (like minimal template)
                                if (config.templateType !== "modern") {
                                    this.appendMessageToUI(
                                        "assistant",
                                        message,
                                    );
                                }
                                this.chatUI.showSuggestedQuestions(
                                    config.suggestedQuestions,
                                );
                            } else {
                                this.connectToHumanButton();
                            }
                            this.chatUI.enableTextInputAndSendButton();
                        },
                        onSubmitFeedback: async (
                            sessionId: string,
                            rating: number,
                        ): Promise<boolean> => {
                            return await this.signalR.submitFeedback(
                                sessionId,
                                rating,
                            );
                        },
                    };

                    this.chatUI = new this.chatUIModule.ChatUI(this.shadowRoot);
                    this.chatUI.initializeUI(chatConfig); // Pass config to initializeUI
                    this.chatUI.initializeChatMessages(
                        config.welcomeMessage,
                        config.suggestedQuestions,
                        this.history,
                    );
                    // Set session ID for feedback tracking
                    if (this.sessionId) {
                        this.chatUI.setSessionId(this.sessionId);
                    }
                    this.setupEventListeners();
                    if (this.audioModule && config.voiceChat) {
                        this.audioManager = new this.audioModule.AudioManager(
                            this.signalR,
                            this.chatUI,
                        );
                        await this.audioManager.initialize(this.baseUrl);
                        this.audioManager.voiceName = config.voiceName;
                        this.audioManager.voiceLanguage = config.voiceLanguage;
                        this.audioManager.sttCandidateWordList = config.sttCandidateWordList ?? [];
                        this.audioManager.onCallEnded = () => {
                            this.isCurrentSessionVoice = false;
                            this.voiceCallAborted = true;
                            this.cancelVoiceAssistantRendering({
                                completeBubbleWithFullMessage: true,
                                persistFullMessage: true,
                            });
                            if (this.toolCallVoicePromptDebounceId !== null) {
                                window.clearTimeout(this.toolCallVoicePromptDebounceId);
                                this.toolCallVoicePromptDebounceId = null;
                            }
                            this.resetAssistantStreamingState();
                            this.chatUI.hideTypingIndicator();
                            this.chatUI.stopVoiceChat();
                            this.chatUI.enableTextInputAndSendButton();
                        };
                        this.audioManager.onVoiceUserMessage = (message: string) => {
                            this.cancelVoiceAssistantRendering({
                                completeBubbleWithFullMessage: true,
                                persistFullMessage: true,
                            });
                            this.resetAssistantStreamingState();
                            this.chatUI.removeSuggestedQuestions();
                            this.appendMessageToUI("user", message, null, false, true, true);
                            this.chatUI.showTypingIndicator(
                                this.getRandomPhraseSet(this.defaultLoadingPhraseSets),
                            );
                        };
                        this.audioManager.onVoiceToken = (token: string) => {
                            this.handleReceiveToken(token, true);
                        };
                        this.audioManager.showGreetingInChat = (message: string) => {
                            this.appendMessageToUI("assistant", message, null, false, true, true);
                        };
                    }
                    this.isUIInitialized = true;
                } catch (error) {
                    console.error("UI initialization failed:", error);
                    this.handleInitializationError();
                }
            }
        } catch (error) {
            console.error(
                `Failed to fetch persona config for ${this.selectedPersonaId}:`,
                error,
            );
            this.handleInitializationError();
        }
    }

    private handleInitializationError() {
        const errorElement = document.createElement("div");
        errorElement.className = "error-message";
        errorElement.textContent =
            "Failed to initialize messenger. Please check your connection and try again.";
        if (this.shadowRoot) {
            this.shadowRoot.appendChild(errorElement);
        }
    }

    private async initializeSignalRConnection() {
        // Return a promise that resolves only after connection is established
        return new Promise<void>((resolve, reject) => {
            const callbacks = {
                onPersonas: this.handlePersonas.bind(this),
                onReceiveMessage: this.handleReceiveMessage.bind(this),
                onReceiveProductCards: this.handleReceiveProductCards.bind(this),
                onReceiveToken: this.handleReceiveToken.bind(this),
                onAgentConnected: this.handleAgentConnected.bind(this),
                onAgentLeft: this.handleAgentLeft.bind(this),
                onTyping: this.handleTyping.bind(this),
                onToolCalls: this.handleToolCalls.bind(this),
                onReceiveAudioResponse: this.handleAudioResponse.bind(this),
                onReceiveAudioChatSessionId:
                    this.handleAudioChatSessionId.bind(this),
                onUserTurn: this.handleUserTurn.bind(this),
                onConnectionClose: this.handleConnectionClose.bind(this),
                onError: (errorMessage: string, error?: any) => {
                    console.error("EM SignalR Error:", errorMessage, error);
                    this.handleSignalRError(errorMessage, error);
                    reject(new Error(`SignalR error: ${errorMessage}`));
                },
                onConnected: async () => {
                    //console.log("SignalR connected successfully");
                    await this.handleSignalRConnected();
                    resolve(); // Resolve only after connected and any post-connect logic (e.g., activateSession)
                },
            };

            this.signalR.initialize(callbacks);
        });
    }

    private async handleSignalRConnected() {
        if (this.history.length > 0) {
            await this.signalR.activateSession();
        }
    }

    private handleSignalRError(errorMessage: string, error?: any) {
        console.error("EM SignalR Error:", errorMessage, error);
        if (errorMessage == "Failed to send message") {
            this.handleReceiveMessage(
                "",
                `⚠️ Server busy. Please try again later.`,
                "assistant",
                false,
                false,
            );
        } else {
            //'Reconnection failed' 'Failed to connect to human agent' 'Reconnection failed'
            this.handleConnectionClose();
        }
    }

    private handleConnectionClose() {
        this.resetAssistantStreamingState();
        this.chatUI.stopVoiceChat();
        this.chatUI.showRefreshButton();
    }

    private handlePersonas(personas: any[]) {
        // Handle multiple personas logic
    }

    // -------------------------
    // 💬 Handle incoming token from backend
    // -------------------------
    private handleReceiveToken(token: string, isVoice = false) {
        if (!token || this.agentId !== null) {
            return;
        }

        if (this.voiceCallAborted) {
            return;
        }

        // For voice sessions, tokens are suppressed — text is driven by wordBoundary sync
        if (isVoice || this.isCurrentSessionVoice) {
            return;
        }

        this.chatUI.hideTypingIndicator();

        // Start streaming assistant if not already
        if (!this.isStreamingAssistant) {
            this.isStreamingAssistant = true;
            this.streamingAssistantBuffer = "";
            this.pendingAssistantTokenBuffer = "";
            this.suppressAssistantStream = false;
            this.appendMessageToUI("assistant", "", null, false, false, false);
            this.chatUI?.setAssistantStreamingState?.(true);
        }

        this.pendingAssistantTokenBuffer += token;
        this.scheduleAssistantStreamFlush();
    }

    // -------------------------
    // 💬 Handle final message from backend
    // -------------------------
    private async handleReceiveMessage(
        sessionId: string,
        message: string,
        role: string,
        switchToAi: boolean,
        connectToHumanAssistant: boolean,
    ) {
        if (this.voiceCallAborted && role === 'user') {
            // Only skip user messages that were part of the aborted voice call
            // Allow assistant responses to be displayed normally
            this.voiceCallAborted = false;
            this.chatUI.enableTextInputAndSendButton();
            return;
        }
        // Reset voiceCallAborted flag for assistant messages to ensure they display
        if (this.voiceCallAborted && role === 'assistant') {
            this.voiceCallAborted = false;
        }

        const wasVoice = this.isCurrentSessionVoice;
        this.isVoiceResponse = false;
        this.chatUI.hideTypingIndicator();
        this.chatUI.enableTextInputAndSendButton();

        if (role === 'assistant' && this.isCurrentSessionVoice && this.audioManager) {
            console.log('[ExcellyMessenger] voice response received, speaking:', message?.slice(0, 100));
            // Reset any stale render timers, then create a fresh voice-render session.
            const voiceRenderSessionId = this.startVoiceAssistantRenderingSession();
            this.activeVoiceRenderMessage = message;
            this.activeVoiceRenderMessageSessionId = voiceRenderSessionId;
            this.activeVoiceRenderPersisted = false;
            // Create the message bubble empty, then fill word-by-word in sync with audio
            this.chatUI.hideTypingIndicator();
            this.appendMessageToUI("assistant", "", null, false, false, true);
            this.chatUI?.setAssistantStreamingState?.(true);

            const words = message.split(/\s+/).filter(Boolean);
            let wordIndex = 0;

            this.audioManager.onWordBoundary = (_word: string, _offsetMs: number) => {
                if (this.activeVoiceRenderSessionId !== voiceRenderSessionId) {
                    return;
                }
                if (wordIndex < words.length) {
                    this.appendMessageToUI(
                        "assistant",
                        (wordIndex === 0 ? "" : " ") + words[wordIndex],
                        null,
                        true,
                        false,
                        true,
                    );
                    wordIndex++;
                }
            };

            const speechPromise = this.audioManager.speakResponse(message, {
                allowInterrupt: true,
                interruptWhenReady: true,
            });
            // Persist/cleanup when speech finishes (with a fallback timeout guard).
            const totalWords = words.length;
            void this.finalizeVoiceAssistantRenderingAfterSpeech(
                voiceRenderSessionId,
                message,
                totalWords,
                speechPromise,
            );
            return;
        }

        if (role === "assistant" && this.isStreamingAssistant) {
            this.flushAssistantStreamTokens();

            if (
                typeof message === "string" &&
                message.startsWith(this.streamingAssistantBuffer)
            ) {
                const remaining = message.slice(
                    this.streamingAssistantBuffer.length,
                );
                if (remaining.length > 0) {
                    this.appendMessageToUI(role, remaining, null, true, false);
                }
            } else if (
                typeof message === "string" &&
                message !== this.streamingAssistantBuffer
            ) {
                console.warn(
                    "Streaming and final assistant messages differ; using final message for history.",
                );
                this.chatUI.replaceLastAssistantMessage(message);
            }

            this.persistMessageToHistory(role, message, wasVoice);
            this.resetAssistantStreamingState();
        } else {
            this.appendMessageToUI(role, message, null, false, true, wasVoice);
        }

        // Human agent connection handling
        if (connectToHumanAssistant) {
            if (!this.displayWelcomeScreen) {
                if (!this.isUserDetailsSubmitted) {
                    this.chatUI.showUserDetailsForm(true);
                } else {
                    this.connectToHumanButton();
                }
            } else {
                this.connectToHumanButton();
            }
        }

        // Switch back to AI agent if needed
        if (switchToAi) {
            this.agentId = null;
        }
    }

    private handleReceiveProductCards(sessionId: string, products: ProductCardPayload[]) {
        if (!products || products.length === 0) {
            return;
        }

        if (this.sessionId && sessionId && this.sessionId !== sessionId) {
            return;
        }

        // During voice TTS word-by-word rendering, delay card insertion to avoid
        // re-render loops that repeatedly reload product images.
        if (this.isVoiceAssistantRendering) {
            this.pendingVoiceProductCards = products;
            return;
        }

        this.renderProductCardsMessage(products);
    }

    private renderProductCardsMessage(products: ProductCardPayload[]) {
        const signature = JSON.stringify(
            products.map((p) => ({
                t: p.title ?? "",
                pr: p.price ?? "",
                u: p.url ?? "",
                h: p.handle ?? "",
                i: p.imageUrl ?? "",
            })),
        );
        const now = Date.now();
        if (
            this.lastProductCardsSignature === signature &&
            now - this.lastProductCardsAtMs < 4000
        ) {
            return;
        }
        this.lastProductCardsSignature = signature;
        this.lastProductCardsAtMs = now;

        const commerceBlocks = products
            .map((product) => {
                const title = (product.title ?? "").trim();
                const price = (product.price ?? "").trim();
                const linkOrHandle =
                    (product.url ?? "").trim() ||
                    (product.handle ?? "").trim();
                const imageUrl = (product.imageUrl ?? "").trim();

                if (!title && !price && !linkOrHandle && !imageUrl) {
                    return "";
                }

                const lines = [
                    `Product: ${title || "Product"}`,
                    `Handle: ${linkOrHandle}`,
                    `Price: ${price || "N/A"}`,
                ];

                if (imageUrl) {
                    lines.push(`Image: ${imageUrl}`);
                }

                return lines.join("\n");
            })
            .filter(Boolean);

        if (commerceBlocks.length === 0) {
            return;
        }

        const commerceMessage = `\n[Commerce products]\n${commerceBlocks.join("\n\n")}`;
        this.appendMessageToUI("assistant-cards", commerceMessage, null, false, true);
    }

    connectToHumanButton() {
        this.signalR.connectToAgent(this.sessionId, this.selectedPersonaId);
    }

    private handleAgentConnected(
        agentId: string,
        agentName: string,
        agentConnectedMessage: string,
    ) {
        this.resetAssistantStreamingState();

        const agentButton = this.shadowRoot?.getElementById("agentButton");
        if (agentButton) {
            agentButton.remove();
        }

        this.agentId = agentId;
        this.agentName = agentName;
        this.appendMessageToUI("transfer", "Human agent");
        this.appendMessageToUI("system", agentConnectedMessage);
    }

    private handleAgentLeft() {
        this.resetAssistantStreamingState();

        this.agentId = null;
        this.agentName = null;
        this.appendMessageToUI("transfer", "AI agent");
    }

    private handleTyping() {
        this.chatUI.showHumanTypingIndicator(this.agentName);
    }
    private handleToolCalls(..._args: any[]) {
        this.chatUI.showTypingIndicator(
            this.getOrderedPhrasesFromRandomLists(
                this.toolCallLoadingPhraseSets,
            ),
            "tool",
        );
        this.chatUI.enableTextInputAndSendButton();

        if (
            !this.isCurrentSessionVoice ||
            !this.audioManager ||
            this.isVoiceAssistantRendering
        ) {
            return;
        }

        if (this.toolCallVoicePromptDebounceId !== null) {
            window.clearTimeout(this.toolCallVoicePromptDebounceId);
            this.toolCallVoicePromptDebounceId = null;
        }

        this.toolCallVoicePromptDebounceId = window.setTimeout(() => {
            this.toolCallVoicePromptDebounceId = null;

            const now = Date.now();
            if (now - this.lastToolCallVoicePromptAtMs < 12000) {
                return;
            }

            let promptIndex = Math.floor(
                Math.random() * this.toolCallVoicePrompts.length,
            );
            if (
                this.toolCallVoicePrompts.length > 1 &&
                promptIndex === this.lastToolCallVoicePromptIndex
            ) {
                promptIndex =
                    (promptIndex + 1 + Math.floor(Math.random() * (this.toolCallVoicePrompts.length - 1))) %
                    this.toolCallVoicePrompts.length;
            }
            const prompt = this.toolCallVoicePrompts[promptIndex];

            this.lastToolCallVoicePromptAtMs = now;
            this.lastToolCallVoicePromptIndex = promptIndex;
            this.audioManager.speakResponse(prompt, {
                allowInterrupt: false,
                expressive: true,
                rate: "-12%",
            });
        }, 1200);
    }

    private async handleAudioResponse(
        audioChatSessionId: string,
        processedAudioBase64: string,
    ) {
        const audioData = this.base64ToArrayBuffer(processedAudioBase64);
        await this.audioManager.playAudioData(audioData);
    }

    private async handleAudioChatSessionId(audioChatSessionId: string) {
        this.audioChatSessionId = audioChatSessionId;
        this.chatUI.stopRingingtone();
        await this.audioManager.startRecording();
    }

    private async handleUserTurn() {
        await this.audioManager.userTurn();
    }

    private setupEventListeners() {
        const toggleButton = this.shadowRoot?.getElementById("toggleButton");
        const closeButton = this.shadowRoot?.querySelector(".chat-close");
        const sendButton = this.shadowRoot?.getElementById("sendButton");
        const userInput = this.shadowRoot?.getElementById(
            "userInput",
        ) as HTMLInputElement;
        const voiceChatButton = this.shadowRoot?.getElementById(
            "voiceChatButton",
        ) as HTMLButtonElement;

        toggleButton?.addEventListener("click", () => {
            this.chatUI.toggleChat();
        });
        closeButton?.addEventListener("click", () => {
            this.chatUI.toggleChat();
        });
        
        sendButton?.addEventListener("click", () => {
            const message = userInput?.value;
            if (message) {
                this.sendMessage(message);
                if (userInput) userInput.value = "";
            }
        });

        userInput?.addEventListener("keypress", (e) => {
            if (e.key === "Enter") {
                const message = userInput.value;
                if (message) {
                    this.sendMessage(message);
                    userInput.value = "";
                }
            } else {
                this.signalR.sendTyping(this.selectedPersonaId, "user");
            }
        });

        voiceChatButton?.addEventListener("click", async () => {
            if (voiceChatButton.classList.contains("listening")) {
                await this.audioManager.cutCall();
                return;
            }
            if (!this.displayWelcomeScreen) {
                this.signalR.submitSessionData(
                    this.selectedPersonaId,
                    this.name,
                    this.email,
                    this.phone,
                );
            }
            this.isCurrentSessionVoice = true;
            this.voiceCallAborted = false;
            await this.chatUI.toggleVoiceChat();
            this.isVoiceResponse = false;
            
            this.audioManager.getCall(this.welcomeMessage, this.history.length > 0);
            this.setupHangupButtonListener();
        });

        this.shadowRoot?.addEventListener("click", async (e) => {
            const target = e.target as HTMLElement;
            if (target.id === "refreshButton") {
                target.setAttribute("disabled", "true");
                target.textContent = "Reconnecting...";
                try {
                    await this.signalR.reconnect();
                    this.chatUI.hideRefreshButton();
                } catch (error) {
                    console.error("Reconnection failed", error);
                    target.removeAttribute("disabled");
                    target.textContent = "RECONNECT";
                }
            }
        });
    }

    
    private setupHangupButtonListener() {
        setTimeout(() => {
            const hangupBtn = this.shadowRoot?.getElementById("hangupButton") as HTMLButtonElement;
            if (hangupBtn) {
                hangupBtn.onclick = async () => {
                    await this.audioManager.cutCall();
                };
            }
        }, 100);
    }

    private async sendMessage(message: string) {
        this.resetAssistantStreamingState();

        if (this.history.length === 0) {
            if (!this.displayWelcomeScreen) {
                this.signalR.submitSessionData(
                    this.selectedPersonaId,
                    this.name,
                    this.email,
                    this.phone,
                );
            }
            this.chatUI.removeSuggestedQuestions();
        }
        this.appendMessageToUI("user", message);
        if (this.agentId == null) {
            this.chatUI.showTypingIndicator(
                this.getRandomPhraseSet(this.defaultLoadingPhraseSets),
            );
            this.chatUI.disableTextInputAndSendButton();
        }
        await this.signalR.sendMessage(
            message,
            this.selectedPersonaId || "",
            this.agentId,
            "",
            "user",
            this.audioManager?.isVoiceMode ?? false,
        );
    }

    private appendMessageToUI(
        role: string,
        message: string,
        history: any = null,
        isUpdate = false,
        persistHistory = true,
        isVoice = false,
    ) {
        this.chatUI.appendMessage(role, message, history, isUpdate, isVoice);

        if (persistHistory && role !== "error" && role !== "system") {
            this.history.push({ role: role, content: message, isVoice: isVoice || undefined });
            if (this.sessionId) {
                this.saveChatHistory(this.sessionId, this.history);
            } else {
                console.warn("sessionId is null, skipping saveChatHistory");
            }
        }
    }

    private persistMessageToHistory(role: string, message: string, isVoice = false) {
        if (role === "error" || role === "system") {
            return;
        }

        this.history.push({ role: role, content: message, isVoice: isVoice || undefined });
        if (this.sessionId) {
            this.saveChatHistory(this.sessionId, this.history);
        } else {
            console.warn("sessionId is null, skipping saveChatHistory");
        }
    }

    private scheduleAssistantStreamFlush() {
        if (this.assistantStreamFrameRequestId !== null) {
            return;
        }

        this.assistantStreamFrameRequestId = window.requestAnimationFrame(
            () => {
                this.assistantStreamFrameRequestId = null;
                this.flushAssistantStreamTokens();
            },
        );
    }

    private flushAssistantStreamTokens() {
        if (!this.pendingAssistantTokenBuffer) {
            return;
        }

        const chunk = this.pendingAssistantTokenBuffer;
        this.pendingAssistantTokenBuffer = "";
        const nextBuffer = this.streamingAssistantBuffer + chunk;
        this.streamingAssistantBuffer = nextBuffer;

        if (!this.suppressAssistantStream && this.looksLikeCommerceInvoke(nextBuffer)) {
            this.suppressAssistantStream = true;
            this.chatUI?.replaceLastAssistantMessage?.("");
            return;
        }

        if (!this.suppressAssistantStream) {
            this.appendMessageToUI("assistant", chunk, null, true, false);
        }

        if (this.pendingAssistantTokenBuffer.length > 0) {
            this.scheduleAssistantStreamFlush();
        }
    }

    private resetAssistantStreamingState() {
        this.cancelVoiceAssistantRendering();
        if (this.assistantStreamFrameRequestId !== null) {
            window.cancelAnimationFrame(this.assistantStreamFrameRequestId);
            this.assistantStreamFrameRequestId = null;
        }

        this.pendingAssistantTokenBuffer = "";
        this.isStreamingAssistant = false;
        this.streamingAssistantBuffer = "";
        this.suppressAssistantStream = false;
        this.chatUI?.setAssistantStreamingState?.(false);
    }

    private startVoiceAssistantRenderingSession(): number {
        this.cancelVoiceAssistantRendering({
            completeBubbleWithFullMessage: true,
            persistFullMessage: true,
        });
        this.isVoiceAssistantRendering = true;
        this.activeVoiceRenderSessionId += 1;
        return this.activeVoiceRenderSessionId;
    }

    private cancelVoiceAssistantRendering(options?: {
        completeBubbleWithFullMessage?: boolean;
        persistFullMessage?: boolean;
    }) {
        const shouldCompleteBubble =
            options?.completeBubbleWithFullMessage ?? false;
        const shouldPersist = options?.persistFullMessage ?? false;

        if (
            shouldCompleteBubble &&
            this.isVoiceAssistantRendering &&
            this.activeVoiceRenderMessage &&
            this.activeVoiceRenderMessageSessionId ===
                this.activeVoiceRenderSessionId
        ) {
            this.chatUI?.replaceLastAssistantMessage?.(
                this.activeVoiceRenderMessage,
            );
            if (shouldPersist && !this.activeVoiceRenderPersisted) {
                this.persistMessageToHistory(
                    "assistant",
                    this.activeVoiceRenderMessage,
                    true,
                );
                this.activeVoiceRenderPersisted = true;
            }
        }

        // Invalidate any in-flight callbacks that already left the timer queue.
        this.activeVoiceRenderSessionId += 1;

        if (this.voiceRenderCleanupTimeoutId !== null) {
            window.clearTimeout(this.voiceRenderCleanupTimeoutId);
            this.voiceRenderCleanupTimeoutId = null;
        }

        if (this.voiceRenderWordTimeoutIds.length > 0) {
            for (const timeoutId of this.voiceRenderWordTimeoutIds) {
                window.clearTimeout(timeoutId);
            }
            this.voiceRenderWordTimeoutIds = [];
        }

        if (this.audioManager) {
            this.audioManager.onWordBoundary = null;
        }

        this.isVoiceAssistantRendering = false;
        this.voiceRenderFinalizeInFlight = false;
        this.activeVoiceRenderMessage = null;
        this.activeVoiceRenderMessageSessionId = null;
        this.activeVoiceRenderPersisted = false;
        this.chatUI?.setAssistantStreamingState?.(false);
    }

    private async finalizeVoiceAssistantRenderingAfterSpeech(
        voiceRenderSessionId: number,
        message: string,
        totalWords: number,
        speechPromise: Promise<void>,
    ) {
        if (!this.audioManager || this.voiceRenderFinalizeInFlight) {
            return;
        }

        this.voiceRenderFinalizeInFlight = true;
        try {
            const fallbackAfterMs =
                totalWords > 0 ? Math.max(10000, totalWords * 900) : 7000;

            await Promise.race([
                speechPromise,
                new Promise<void>((resolve) => {
                    this.voiceRenderCleanupTimeoutId = window.setTimeout(
                        resolve,
                        fallbackAfterMs,
                    );
                }),
            ]);

            if (this.activeVoiceRenderSessionId !== voiceRenderSessionId) {
                return;
            }

            if (!this.activeVoiceRenderPersisted) {
                this.persistMessageToHistory("assistant", message, true);
                this.activeVoiceRenderPersisted = true;
            }
            this.cancelVoiceAssistantRendering({
                completeBubbleWithFullMessage: false,
                persistFullMessage: false,
            });
            if (
                this.pendingVoiceProductCards &&
                this.pendingVoiceProductCards.length > 0
            ) {
                this.renderProductCardsMessage(this.pendingVoiceProductCards);
                this.pendingVoiceProductCards = null;
            }
        } finally {
            this.voiceRenderFinalizeInFlight = false;
        }
    }

    private looksLikeCommerceInvoke(value: string): boolean {
        const trimmed = value.trim();
        if (!trimmed.startsWith("{")) return false;
        return /"commerceInvoke"\s*:\s*\{/.test(trimmed);
    }

    private getOrSetSessionId(): string {
        const personaId = this.getAttribute("personaId");
        this.selectedPersonaId = personaId;
        const sessionIdKey = "excelly-session-id";
        const personaIdsKey = "excelly-persona-ids";
        const sessionPersonaMapKey = "excelly-session-persona-map";

        // Retrieve stored data from sessionStorage
        const storedSessionId = sessionStorage.getItem(sessionIdKey);
        const storedPersonaIds = sessionStorage.getItem(personaIdsKey);
        const storedSessionPersonaMap =
            sessionStorage.getItem(sessionPersonaMapKey);

        // Parse stored persona IDs
        let personaIds: string[] = [];
        try {
            personaIds = storedPersonaIds ? JSON.parse(storedPersonaIds) : [];
            if (!Array.isArray(personaIds)) {
                console.error(
                    "getOrSetSessionId: Invalid personaIds format, resetting to []",
                );
                personaIds = [];
            }
        } catch (e) {
            console.error("getOrSetSessionId: Failed to parse personaIds:", e);
            personaIds = [];
        }

        // Parse session-to-persona map
        let sessionMap: { [personaId: string]: string } = {};
        try {
            sessionMap = storedSessionPersonaMap
                ? JSON.parse(storedSessionPersonaMap)
                : {};
            if (typeof sessionMap !== "object" || sessionMap === null) {
                console.error(
                    "getOrSetSessionId: Invalid sessionMap format, resetting to {}",
                );
                sessionMap = {};
            }
        } catch (e) {
            console.error("getOrSetSessionId: Failed to parse sessionMap:", e);
            sessionMap = {};
        }

        // If no selectedPersonaId or it's not in the sessionMap, generate a new sessionId
        if (!this.selectedPersonaId || !sessionMap[this.selectedPersonaId]) {
            const newSessionId = this.generateID();
            sessionStorage.setItem(sessionIdKey, newSessionId);
            if (this.selectedPersonaId) {
                // Add to personaIds if not already present
                if (!personaIds.includes(this.selectedPersonaId)) {
                    personaIds.push(this.selectedPersonaId);
                    sessionStorage.setItem(
                        personaIdsKey,
                        JSON.stringify(personaIds),
                    );
                }
                // Map the new sessionId to the personaId
                sessionMap[this.selectedPersonaId] = newSessionId;
                sessionStorage.setItem(
                    sessionPersonaMapKey,
                    JSON.stringify(sessionMap),
                );
            }
            return newSessionId;
        }

        // Reuse the sessionId mapped to the selectedPersonaId
        const existingSessionId = sessionMap[this.selectedPersonaId];
        sessionStorage.setItem(sessionIdKey, existingSessionId);
        // Add to personaIds if not already present
        if (!personaIds.includes(this.selectedPersonaId)) {
            personaIds.push(this.selectedPersonaId);
            sessionStorage.setItem(personaIdsKey, JSON.stringify(personaIds));
        }
        return existingSessionId;
    }

    private generateID(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
            /[xy]/g,
            function (c) {
                const r = (Math.random() * 16) | 0;
                const v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            },
        );
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private saveChatHistory(sessionId: string, chatHistory: any[]) {
        const chatHistoryKey = `chat-history-${sessionId}-${this.selectedPersonaId}`;
        sessionStorage.setItem(chatHistoryKey, JSON.stringify(chatHistory));
    }

    private loadChatHistory(sessionId: string): any[] {
        const chatHistoryKey = `chat-history-${sessionId}-${this.selectedPersonaId}`;
        const initialSubmitKey = `initial-submit-${sessionId}-${this.selectedPersonaId}`;
        const storedHistory = sessionStorage.getItem(chatHistoryKey);
        let initialSubmitValue = sessionStorage.getItem(initialSubmitKey);
        if (initialSubmitValue === null) {
            initialSubmitValue = "true";
        }
        this.initialSubmit = initialSubmitValue === "true";
        if (storedHistory) {
            return JSON.parse(storedHistory);
        }
        return [];
    }
}

customElements.define("excelly-messenger", ExcellyMessenger);
